/**
 * Coach social-field normalization.
 *
 * Coaches entered their social links as free text at sign-up, so a single field
 * can hold a full URL, a URL missing its scheme (`instagram.com/jane`), an
 * `@handle`, or a bare username (`jane.doe`). A bare username renders as a
 * relative anchor the browser resolves against the current page — a 404 — and
 * is never treated as external. This module coerces every shape into an absolute
 * `https://` URL on the right platform so the anchor is always a working
 * external link.
 *
 * `toExternalHref` (in link-rel.ts) handles the platform-agnostic case (bare
 * hosts → https). This adds the platform knowledge needed to turn a bare handle
 * into a real profile URL.
 */

import { toExternalHref } from "~/lib/shared/link-rel";

/**
 * Platforms whose fields carry a username/handle. `website` is intentionally
 * excluded — it is platform-agnostic and only needs `toExternalHref`.
 */
export type SocialPlatform = "instagram" | "facebook" | "linkedin" | "youtube";

interface PlatformConfig {
  /** Hosts that mean "this value is already a URL for this platform". */
  readonly hosts: readonly string[];
  /** Builds an absolute profile URL from a bare handle / on-platform path. */
  readonly fromHandle: (handle: string) => string;
}

const stripLeadingAt = (value: string): string => value.replace(/^@/u, "");

const LINKEDIN_PATH_PREFIXES = [
  "in/",
  "company/",
  "school/",
  "pub/",
  "showcase/",
];
const YOUTUBE_PATH_PREFIXES = ["channel/", "c/", "user/"];
const YOUTUBE_CHANNEL_ID_PATTERN = /^UC[\w-]{22}$/u;

const PLATFORMS: Record<SocialPlatform, PlatformConfig> = {
  instagram: {
    hosts: ["instagram.com", "instagr.am"],
    fromHandle: (handle) => `https://instagram.com/${stripLeadingAt(handle)}`,
  },
  facebook: {
    hosts: ["facebook.com", "fb.com", "fb.me"],
    fromHandle: (handle) => `https://facebook.com/${stripLeadingAt(handle)}`,
  },
  linkedin: {
    hosts: ["linkedin.com", "lnkd.in"],
    fromHandle: (handle) => {
      const cleaned = stripLeadingAt(handle);
      const hasPrefix = LINKEDIN_PATH_PREFIXES.some((prefix) =>
        cleaned.toLowerCase().startsWith(prefix),
      );
      // A bare token is a personal vanity name; default it to the /in/ path.
      return `https://linkedin.com/${hasPrefix ? cleaned : `in/${cleaned}`}`;
    },
  },
  youtube: {
    hosts: ["youtube.com", "youtu.be"],
    fromHandle: (handle) => {
      // `@name` is YouTube's canonical handle form — keep it as-is.
      if (handle.startsWith("@")) return `https://youtube.com/${handle}`;
      const lower = handle.toLowerCase();
      if (YOUTUBE_PATH_PREFIXES.some((prefix) => lower.startsWith(prefix))) {
        return `https://youtube.com/${handle}`;
      }
      if (YOUTUBE_CHANNEL_ID_PATTERN.test(handle)) {
        return `https://youtube.com/channel/${handle}`;
      }
      return `https://youtube.com/@${handle}`;
    },
  },
};

const ABSOLUTE_URL_PATTERN = /^(https?:)?\/\//iu;
const SPECIAL_SCHEME_PATTERN = /^(mailto|tel|sms):/iu;
const LEADING_SLASH_PATTERN = /^\/+/u;
const TRAILING_SLASH_PATTERN = /\/+$/u;

/** Lowercased first path segment — the host candidate — of a scheme-less value. */
const firstSegment = (value: string): string =>
  (value.split(/[/?#]/u)[0] ?? "").toLowerCase();

const matchesHost = (segment: string, hosts: readonly string[]): boolean =>
  hosts.some((host) => segment === host || segment.endsWith(`.${host}`));

/**
 * Returns `url` only when it parses as an absolute http(s) URL, otherwise "".
 * The last line of defence: a coach's unsalvageable input (a lone `https://`,
 * or junk that resolves to a host-less URL) is dropped to blank rather than
 * surfaced as a value that fails downstream `z.string().url()` and 500s a
 * profile save — matching the view, which renders nothing for a blank field.
 */
const toValidHttpUrlOrEmpty = (url: string): string => {
  if (!url) return "";
  try {
    const { protocol } = new URL(url);
    return protocol === "https:" || protocol === "http:" ? url : "";
  } catch {
    return "";
  }
};

/**
 * Normalizes a coach-supplied social field into an absolute external URL.
 *
 * Idempotent: a value that is already a clean absolute URL is returned as-is, so
 * this is safe to run on every render as well as at the form boundary.
 *
 * Always returns either "" or a parseable http(s) URL, so callers (renderers
 * AND the profile write schema) can trust the result without re-validating.
 */
export const normalizeSocialUrl = (
  raw: string | null | undefined,
  platform: SocialPlatform,
): string => {
  const trimmed = raw?.trim();
  if (!trimmed) return "";

  // Already absolute (or protocol-relative), or a non-web scheme: defer to the
  // generic coercion, which leaves these untouched.
  if (
    ABSOLUTE_URL_PATTERN.test(trimmed) ||
    SPECIAL_SCHEME_PATTERN.test(trimmed)
  ) {
    return toValidHttpUrlOrEmpty(toExternalHref(trimmed));
  }

  const config = PLATFORMS[platform];
  const withoutLeadingSlash = trimmed.replace(LEADING_SLASH_PATTERN, "");
  const segment = firstSegment(withoutLeadingSlash);

  // Treat the value as a scheme-less URL (just prepend https) when the leading
  // segment is a known platform host (`instagram.com/jane`), OR when it both
  // contains a path (`/`) and looks like a hostname (has a dot) — e.g. a coach
  // who pasted `linktr.ee/jane` into the Instagram field.
  //
  // The path requirement is the key guard. Platform usernames legitimately
  // contain dots (`jane.doe`), so a dotted token on its own must stay a handle.
  // And platform paths like `in/jane` or `c/Name` have a dot-free leading
  // segment, so they fall through to the handle builder rather than becoming
  // `https://in/jane`.
  const looksLikeSchemelessUrl =
    matchesHost(segment, config.hosts) ||
    (withoutLeadingSlash.includes("/") && segment.includes("."));
  if (looksLikeSchemelessUrl) {
    return toValidHttpUrlOrEmpty(toExternalHref(withoutLeadingSlash));
  }

  // Otherwise it's a bare handle or on-platform path.
  return toValidHttpUrlOrEmpty(
    config.fromHandle(withoutLeadingSlash.replace(TRAILING_SLASH_PATTERN, "")),
  );
};

/**
 * Website field normalization: the platform-agnostic absolute-URL coercion,
 * guaranteed valid-or-empty. Exported for the profile write schema so a bad
 * website value is dropped to blank instead of 500-ing the save.
 */
export const normalizeWebsiteUrl = (raw: string | null | undefined): string =>
  toValidHttpUrlOrEmpty(toExternalHref(raw));

export interface ProfileSocialFields {
  website: string | null;
  linkedinProfile: string | null;
  instagramProfile: string | null;
  facebookProfile: string | null;
  youtubeChannel: string | null;
}

/**
 * Normalizes every social field on a coach profile for rendering. `website` is
 * platform-agnostic so it gets the generic absolute-URL coercion; the four
 * platform fields resolve to absolute profile URLs. Blank fields come back as
 * `null` so existing truthiness checks in renderers keep working.
 */
export const normalizeProfileSocials = (
  profile: ProfileSocialFields,
): ProfileSocialFields => ({
  website: normalizeWebsiteUrl(profile.website) || null,
  linkedinProfile:
    normalizeSocialUrl(profile.linkedinProfile, "linkedin") || null,
  instagramProfile:
    normalizeSocialUrl(profile.instagramProfile, "instagram") || null,
  facebookProfile:
    normalizeSocialUrl(profile.facebookProfile, "facebook") || null,
  youtubeChannel: normalizeSocialUrl(profile.youtubeChannel, "youtube") || null,
});
